import { useState } from 'react'
import DataTable from '../../components/DataTable'
import { formatDate } from '../../utils/formatters'
import { mockPets } from '../../data/mockData'
import { CheckCircle, XCircle } from 'lucide-react'

/**
 * Pets Management Page
 * Display and manage all registered pets
 */
export default function PetsPage() {
  const [sortKey, setSortKey] = useState('createdAt')
  const [sortOrder, setSortOrder] = useState('desc')
  const [filterType, setFilterType] = useState('All')

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortOrder('asc')
    }
  }

  const filteredPets =
    filterType === 'All' ? mockPets : mockPets.filter((pet) => pet.type === filterType)

  const petCounts = {
    all: mockPets.length,
    dogs: mockPets.filter((p) => p.type === 'Dog').length,
    cats: mockPets.filter((p) => p.type === 'Cat').length,
    other: mockPets.filter((p) => !['Dog', 'Cat'].includes(p.type)).length,
  }

  const columns = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
    },
    {
      key: 'breed',
      label: 'Breed',
      sortable: false,
    },
    {
      key: 'owner',
      label: 'Owner',
      sortable: false,
    },
    {
      key: 'age',
      label: 'Age (Years)',
      sortable: false,
      render: (value) => `${value}y`,
    },
    {
      key: 'vaccinated',
      label: 'Vaccinated',
      render: (value) => (
        <div className="flex items-center gap-2">
          {value ? (
            <>
              <CheckCircle size={16} className="text-green-600" />
              <span className="text-sm font-medium text-green-700">Yes</span>
            </>
          ) : (
            <>
              <XCircle size={16} className="text-red-600" />
              <span className="text-sm font-medium text-red-700">No</span>
            </>
          )}
        </div>
      ),
    },
    {
      key: 'lastCheckup',
      label: 'Last Checkup',
      render: formatDate,
    },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-pawssible-slate-900">Pets</h1>
        <p className="text-pawssible-slate-600 mt-1">Manage all registered pets</p>
      </div>

      {/* Pet Type Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div
          className={`card cursor-pointer hover:shadow-md transition-shadow ${
            filterType === 'All' ? 'border-pawssible-teal-500 border-2' : ''
          }`}
          onClick={() => setFilterType('All')}
        >
          <p className="text-sm text-pawssible-slate-600">All Pets</p>
          <p className="text-2xl font-bold text-pawssible-slate-900 mt-1">{petCounts.all}</p>
        </div>
        <div
          className={`card cursor-pointer hover:shadow-md transition-shadow ${
            filterType === 'Dog' ? 'border-pawssible-teal-500 border-2' : ''
          }`}
          onClick={() => setFilterType('Dog')}
        >
          <p className="text-sm text-pawssible-slate-600">Dogs</p>
          <p className="text-2xl font-bold text-pawssible-slate-900 mt-1">{petCounts.dogs}</p>
        </div>
        <div
          className={`card cursor-pointer hover:shadow-md transition-shadow ${
            filterType === 'Cat' ? 'border-pawssible-teal-500 border-2' : ''
          }`}
          onClick={() => setFilterType('Cat')}
        >
          <p className="text-sm text-pawssible-slate-600">Cats</p>
          <p className="text-2xl font-bold text-pawssible-slate-900 mt-1">{petCounts.cats}</p>
        </div>
        <div
          className={`card cursor-pointer hover:shadow-md transition-shadow ${
            filterType === 'Other' ? 'border-pawssible-teal-500 border-2' : ''
          }`}
          onClick={() => setFilterType('Other')}
        >
          <p className="text-sm text-pawssible-slate-600">Other</p>
          <p className="text-2xl font-bold text-pawssible-slate-900 mt-1">{petCounts.other}</p>
        </div>
      </div>

      <div className="flex gap-4 flex-wrap">
        <button className="btn-primary">Add Pet</button>
        <button className="btn-secondary">Export CSV</button>
      </div>

      <DataTable
        columns={columns}
        data={filteredPets}
        onSort={handleSort}
        sortKey={sortKey}
        sortOrder={sortOrder}
        pageSize={15}
      />
    </div>
  )
}
